import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { CompliancePageShell } from "../shared/CompliancePageShell";
import { useAddRequirement, useRemoveRequirement, useRequirements } from "./use-requirements";
import { useDocumentTypes } from "../document-types/use-document-types";
import { useDocuments } from "../documents/use-documents";
import { useTosRoleScopes } from "../shared/use-tos-role-scopes";
import { roleScopeLabel } from "../shared/role-scopes";
import type { TosDocumentType, TosRoleScope } from "../shared/compliance-types";
import { resolveComplianceError } from "../shared/resolve-compliance-error";

interface SaveResult {
  readonly added: number;
  readonly removed: number;
  readonly errors: ReadonlyArray<{
    readonly documentTypeId: string;
    readonly action: "add" | "remove";
    readonly message: string;
  }>;
}

export function RequirementsPage() {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const canEdit = currentUser?.role === Role.WANDERFIT_ADMIN;

  const [selectedRole, setSelectedRole] = useState<TosRoleScope>("GUEST");

  const roleScopes = useTosRoleScopes();
  const requirementsQuery = useRequirements();
  const documentTypesQuery = useDocumentTypes();
  const documentsQuery = useDocuments({ roleScope: selectedRole });

  const add = useAddRequirement();
  const remove = useRemoveRequirement();

  // The set of `documentTypeId`s currently required for the selected
  // role. Source of truth = persisted requirements.
  const requiredForRole = useMemo(() => {
    const set = new Set<string>();
    requirementsQuery.data?.forEach((r) => {
      if (r.role === selectedRole) set.add(r.documentTypeId);
    });
    return set;
  }, [requirementsQuery.data, selectedRole]);

  // Local checkbox state (initialised from persisted on every role change).
  const [draftSelected, setDraftSelected] = useState<Set<string> | null>(null);
  const activeSelection = draftSelected ?? requiredForRole;

  // Reset draft when role changes or persisted data updates while no
  // local edit is in flight.
  if (draftSelected === null && requirementsQuery.data) {
    // no-op — `activeSelection` already reflects the persisted set
  }

  const handleToggle = (documentTypeId: string, next: boolean) => {
    const baseline = draftSelected ?? new Set(requiredForRole);
    const newSet = new Set(baseline);
    if (next) newSet.add(documentTypeId);
    else newSet.delete(documentTypeId);
    setDraftSelected(newSet);
  };

  const [saveResult, setSaveResult] = useState<SaveResult | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!draftSelected || saving) return;
    setSaving(true);
    setSaveResult(null);

    const toAdd = [...draftSelected].filter((id) => !requiredForRole.has(id));
    const toRemove = [...requiredForRole].filter((id) => !draftSelected.has(id));

    // Promise.allSettled so a single 409 doesn't hide the other failures.
    // No partial rollback — successes stay, failures are reported, and
    // the requirements query is re-fetched on completion to reconcile
    // the UI state with what actually landed server-side.
    const results = await Promise.allSettled([
      ...toAdd.map((documentTypeId) =>
        add
          .mutateAsync({ role: selectedRole, documentTypeId })
          .then(() => ({ ok: true as const, documentTypeId, action: "add" as const }))
          .catch((err: unknown) => ({
            ok: false as const,
            documentTypeId,
            action: "add" as const,
            message: resolveComplianceError(err, t),
          })),
      ),
      ...toRemove.map((documentTypeId) =>
        remove
          .mutateAsync({ role: selectedRole, documentTypeId })
          .then(() => ({ ok: true as const, documentTypeId, action: "remove" as const }))
          .catch((err: unknown) => ({
            ok: false as const,
            documentTypeId,
            action: "remove" as const,
            message: resolveComplianceError(err, t),
          })),
      ),
    ]);

    const flat = results.flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
    const summary: SaveResult = {
      added: flat.filter((r) => r.ok && r.action === "add").length,
      removed: flat.filter((r) => r.ok && r.action === "remove").length,
      errors: flat
        .filter(
          (
            r,
          ): r is {
            ok: false;
            documentTypeId: string;
            action: "add" | "remove";
            message: string;
          } => !r.ok,
        )
        .map((r) => ({
          documentTypeId: r.documentTypeId,
          action: r.action,
          message: r.message,
        })),
    };

    setSaveResult(summary);
    // Reconcile draft to persisted on success/partial-success — the
    // requirements query refetch happens automatically via the mutation
    // hooks' onSuccess, but the local draft set must be cleared so the
    // next render shows the new "no pending changes" state.
    setDraftSelected(null);
    setSaving(false);
  };

  const dirty =
    draftSelected != null &&
    (draftSelected.size !== requiredForRole.size ||
      [...draftSelected].some((id) => !requiredForRole.has(id)));

  return (
    <CompliancePageShell
      screenTestId="compliance-requirements-screen"
      titleKey="compliance.requirements.title"
      subtitleKey="compliance.requirements.subtitle"
    >
      <div className="space-y-2">
        <label htmlFor="compliance-requirements-role" className="text-sm font-medium">
          {t("compliance.requirements.roleLabel")}
        </label>
        <select
          id="compliance-requirements-role"
          data-testid="compliance-filter-role"
          value={selectedRole}
          onChange={(e) => {
            setSelectedRole(e.target.value as TosRoleScope);
            setDraftSelected(null);
            setSaveResult(null);
          }}
          className="block w-full max-w-xs rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          {roleScopes.scopes.map((scope) => (
            <option key={scope} value={scope}>
              {roleScopeLabel(t, scope)}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">{t("compliance.requirements.staffNote")}</p>
      </div>

      <RequirementsList
        documentTypes={documentTypesQuery.data}
        documents={documentsQuery.data}
        selection={activeSelection}
        canEdit={canEdit}
        onToggle={handleToggle}
      />

      {canEdit && (
        <div className="flex items-center gap-3">
          <Button
            data-testid="compliance-save-requirements-button"
            disabled={!dirty || saving}
            onClick={handleSave}
          >
            {saving ? t("compliance.actions.saving") : t("compliance.actions.save")}
          </Button>
          {dirty && (
            <p className="text-xs text-muted-foreground">
              {t("compliance.requirements.dirtyHint")}
            </p>
          )}
        </div>
      )}

      {saveResult && <SaveResultPanel result={saveResult} />}
    </CompliancePageShell>
  );
}

interface RequirementsListProps {
  readonly documentTypes: readonly TosDocumentType[] | undefined;
  readonly documents:
    | readonly {
        readonly id: string;
        readonly documentTypeId: string;
        readonly version: string;
        readonly state: "DRAFT" | "ACTIVE" | "SCHEDULED_RETIRE" | "RETIRED";
        readonly effectiveFrom: string;
      }[]
    | undefined;
  readonly selection: ReadonlySet<string>;
  readonly canEdit: boolean;
  readonly onToggle: (documentTypeId: string, next: boolean) => void;
}

function RequirementsList({
  documentTypes,
  documents,
  selection,
  canEdit,
  onToggle,
}: RequirementsListProps) {
  const { t } = useTranslation();

  // Show only doc types with a "live" doc for the selected role —
  // anything ACTIVE or SCHEDULED_RETIRE counts. Backend rejects
  // `POST /role-requirements` with NO_ACTIVE_DOCUMENT_FOR_REQUIREMENT
  // for DRAFT or RETIRED targets, so we don't surface them.
  const activeDocByType = useMemo(() => {
    const map = new Map<string, { version: string; effectiveFrom: string }>();
    documents?.forEach((doc) => {
      if (doc.state === "ACTIVE" || doc.state === "SCHEDULED_RETIRE") {
        map.set(doc.documentTypeId, {
          version: doc.version,
          effectiveFrom: doc.effectiveFrom,
        });
      }
    });
    return map;
  }, [documents]);

  if (!documentTypes || !documents) {
    return <p className="text-sm text-muted-foreground">{t("compliance.requirements.loading")}</p>;
  }

  const eligibleTypes = documentTypes.filter((dt) => activeDocByType.has(dt.id));

  if (eligibleTypes.length === 0) {
    return (
      <p data-testid="compliance-requirements-empty" className="text-sm text-muted-foreground">
        {t("compliance.requirements.empty")}
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {eligibleTypes.map((dt) => {
        const active = activeDocByType.get(dt.id)!;
        const checked = selection.has(dt.id);
        return (
          <li
            key={dt.id}
            data-testid={`compliance-requirement-row-${dt.key}`}
            className="flex items-start gap-3 rounded-md border border-border bg-card p-4"
          >
            <input
              type="checkbox"
              id={`req-${dt.id}`}
              data-testid={`compliance-requirement-checkbox-${dt.key}`}
              checked={checked}
              disabled={!canEdit}
              onChange={(e) => onToggle(dt.id, e.target.checked)}
              className="mt-1 h-4 w-4"
            />
            <label htmlFor={`req-${dt.id}`} className="flex-1 cursor-pointer space-y-1">
              <div className="flex items-center gap-2">
                <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{dt.key}</code>
                <span className="text-xs text-muted-foreground">v{active.version}</span>
              </div>
              <p className="font-medium">{dt.name}</p>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

function SaveResultPanel({ result }: { readonly result: SaveResult }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="compliance-requirements-save-summary"
      className="space-y-2 rounded-md border border-border bg-muted/30 p-4"
    >
      <p className="text-sm">
        {t("compliance.requirements.saveSummary.applied", {
          added: result.added,
          removed: result.removed,
        })}
      </p>
      {result.errors.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-sm text-destructive">
          {result.errors.map((e, i) => (
            <li key={i} data-testid={`compliance-requirements-save-error-${i}`}>
              {e.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
